import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { backupRecordsToExcel } from "@/utils/countyResearchBackup";
import {
  isExcludedEntity,
  isBankEntity,
  isBusinessToBusinessTransfer,
  isFamilyTransfer,
} from "./filters";
import type { CountyAdapter, ParsedDocument, PipelineResult } from "./types";

const emptyResult = (): PipelineResult => ({
  totalParsed: 0,
  adapterRejected: 0,
  sourceDuplicatesSkipped: 0,
  excludedFiltered: 0,
  bankFiltered: 0,
  businessFiltered: 0,
  familyFiltered: 0,
  existingSkipped: 0,
  imported: 0,
});

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }
  return "An error occurred";
};

export function useParserPipeline(
  adapter: CountyAdapter,
  onImportComplete?: () => void,
) {
  const [rawText, setRawText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<PipelineResult | null>(null);
  const { toast } = useToast();

  const processDocs = async (parsed: ParsedDocument[]) => {
    const stats = emptyResult();
    stats.totalParsed = parsed.length;
    setProgress(20);

    if (parsed.length === 0) {
      toast({
        title: "No records found",
        description: "Could not parse any records from the pasted text.",
        variant: "destructive",
      });
      return;
    }

    const valid: ParsedDocument[] = [];
    for (const doc of parsed) {
      if (doc.skipReason) {
        stats.adapterRejected++;
        continue;
      }
      if (doc.grantors.length === 0) continue;
      const all = [...doc.grantors, ...doc.grantees];
      if (all.some(isExcludedEntity)) {
        stats.excludedFiltered++;
        continue;
      }
      if (all.some(isBankEntity)) {
        stats.bankFiltered++;
        continue;
      }
      if (
        doc.grantees.length > 0 &&
        isBusinessToBusinessTransfer(doc.grantors, doc.grantees)
      ) {
        stats.businessFiltered++;
        continue;
      }
      if (
        doc.grantees.length > 0 &&
        isFamilyTransfer(doc.grantors, doc.grantees)
      ) {
        stats.familyFiltered++;
        continue;
      }
      valid.push(doc);
    }
    setProgress(50);

    // Normalize a name for dedup: uppercase, strip punctuation, collapse spaces,
    // drop common suffixes/roles so "SMITH, JOHN A" and "Smith John" collide.
    const normalizeName = (n: string): string =>
      n
        .toUpperCase()
        .replace(/[.,;]/g, " ")
        .replace(/\b(JR|SR|II|III|IV|TR|TRUSTEE|MEMBER|EXEC|ETAL|ET AL|ETUX|ET UX)\b/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    const primaryKey = (doc: ParsedDocument): string => {
      const g = doc.grantors[0] ? normalizeName(doc.grantors[0]) : "";
      const e = doc.grantees[0] ? normalizeName(doc.grantees[0]) : "";
      return `${g}|${e}`;
    };

    const seenDocs = new Set<string>();
    const seenParties = new Set<string>();
    const uniqueValid = valid.filter((doc) => {
      if (seenDocs.has(doc.documentNumber)) {
        stats.sourceDuplicatesSkipped++;
        return false;
      }
      const pk = primaryKey(doc);
      if (pk !== "|" && seenParties.has(pk)) {
        stats.sourceDuplicatesSkipped++;
        return false;
      }
      seenDocs.add(doc.documentNumber);
      if (pk !== "|") seenParties.add(pk);
      return true;
    });

    const docNumbers = uniqueValid.map((v) => v.documentNumber);
    const existingDocs: { document_number: string }[] = [];
    for (let i = 0; i < docNumbers.length; i += 200) {
      const chunk = docNumbers.slice(i, i + 200);
      const { data, error } = await supabase
        .from("county_research")
        .select("document_number")
        .is("deleted_at", null)
        .in("document_number", chunk);
      if (error) throw error;
      if (data) existingDocs.push(...data);
    }
    const existingDocSet = new Set(existingDocs.map((e) => e.document_number));

    // Also pull existing grantor/grantee names for this county so we skip the
    // same seller showing up across multiple document numbers (book-pages).
    const { data: existingParties } = await supabase
      .from("county_research")
      .select("grantor_name,grantee_name")
      .eq("county", adapter.countyId)
      .is("deleted_at", null);
    const existingPartySet = new Set<string>();
    for (const row of existingParties || []) {
      const g = (row.grantor_name || "").split(";")[0] || "";
      const e = (row.grantee_name || "").split(";")[0] || "";
      const key = `${normalizeName(g)}|${normalizeName(e)}`;
      if (key !== "|") existingPartySet.add(key);
    }

    setProgress(60);

    const toInsert = uniqueValid.filter((v) => {
      if (existingDocSet.has(v.documentNumber)) {
        stats.existingSkipped++;
        return false;
      }
      const pk = primaryKey(v);
      if (pk !== "|" && existingPartySet.has(pk)) {
        stats.existingSkipped++;
        return false;
      }
      return true;
    });


    if (toInsert.length > 0) {
      const insertData = toInsert.map((doc) => ({
        county: adapter.countyId,
        document_number: doc.documentNumber,
        document_type: doc.documentType || null,
        filing_date: doc.filingDate,
        grantor_name: doc.grantors.join("; "),
        grantee_name: doc.grantees.length ? doc.grantees.join("; ") : null,
        grantor_address: doc.grantorAddress || null,
        grantee_address: doc.granteeAddress || null,
        property_address: doc.propertyAddress || null,
        legal_description: doc.legalDescription || null,
        notes: doc.notes || null,
        is_potential_owner_finance: true,
        confidence_score: adapter.confidenceScore ?? 85,
        detection_reasons: JSON.stringify(
          adapter.detectionReasons ?? [adapter.countyLabel],
        ),
        search_date: new Date().toISOString(),
        status: "new",
        raw_data: (doc.rawData as any) ?? null,
      }));

      const batchSize = 50;
      for (let i = 0; i < insertData.length; i += batchSize) {
        const batch = insertData.slice(i, i + batchSize);
        const { error } = await supabase
          .from("county_research")
          .insert(batch);
        if (error) throw error;
        stats.imported += batch.length;
        setProgress(60 + ((i + batch.length) / insertData.length) * 35);
      }
      backupRecordsToExcel(
        insertData,
        adapter.countyId,
        `${adapter.countyId}_parser`,
      );
    }

    setProgress(100);
    setResult(stats);
    setRawText("");

    toast({
      title: "Import complete",
      description: `Imported ${stats.imported} records from ${adapter.countyLabel}`,
    });

    onImportComplete?.();
  };

  const run = async () => {
    if (!rawText.trim()) {
      toast({
        title: "No data",
        description: `Please paste ${adapter.countyLabel} data to import`,
        variant: "destructive",
      });
      return;
    }
    setIsProcessing(true);
    setProgress(0);
    setResult(null);
    try {
      const parsed = await Promise.resolve(adapter.parse(rawText));
      await processDocs(parsed);
    } catch (error) {
      console.error(`[${adapter.countyId}] import error:`, error);
      toast({
        title: "Import failed",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const runAi = async () => {
    if (!rawText.trim()) {
      toast({
        title: "No data",
        description: `Please paste ${adapter.countyLabel} data to import`,
        variant: "destructive",
      });
      return;
    }
    setIsProcessing(true);
    setProgress(0);
    setResult(null);
    try {
      // Split large pastes into line-based chunks so the AI gateway doesn't
      // time out and each chunk shows visible progress.
      const lines = rawText.split(/\r?\n/);
      const CHUNK_LINES = 60;
      const chunks: string[] = [];
      for (let i = 0; i < lines.length; i += CHUNK_LINES) {
        const chunk = lines.slice(i, i + CHUNK_LINES).join("\n").trim();
        if (chunk.length >= 10) chunks.push(chunk);
      }
      if (chunks.length === 0) chunks.push(rawText);

      const allRecords: any[] = [];
      const failures: string[] = [];
      for (let idx = 0; idx < chunks.length; idx++) {
        setProgress(5 + Math.round((idx / chunks.length) * 45));
        try {
          const { data, error } = await supabase.functions.invoke(
            "ai-county-parser",
            {
              body: {
                text: chunks[idx],
                countyId: adapter.countyId,
                countyLabel: adapter.countyLabel,
              },
            },
          );
          if (error) throw error;
          if (data?.error) throw new Error(data.error);
          if (Array.isArray(data?.records)) allRecords.push(...data.records);
        } catch (chunkErr) {
          console.error(
            `[${adapter.countyId}] AI chunk ${idx + 1}/${chunks.length} failed:`,
            chunkErr,
          );
          failures.push(`Chunk ${idx + 1}: ${getErrorMessage(chunkErr)}`);
        }
      }

      if (allRecords.length === 0) {
        throw new Error(
          failures.length
            ? `AI parser failed on all ${chunks.length} chunk(s). ${failures[0]}`
            : "AI returned no records.",
        );
      }
      if (failures.length > 0) {
        toast({
          title: `Partial AI parse: ${failures.length}/${chunks.length} chunks failed`,
          description: "Continuing with the records that succeeded.",
        });
      }

      const parsed: ParsedDocument[] = allRecords
        .filter((r) => r.documentNumber && r.filingDate && r.grantors?.length)
        .map((r) => ({
          documentNumber: r.documentNumber,
          documentType: r.documentType,
          filingDate: r.filingDate,
          grantors: r.grantors,
          grantees: r.grantees ?? [],
          propertyAddress: r.propertyAddress,
          grantorAddress: r.grantorAddress,
          granteeAddress: r.granteeAddress,
          legalDescription: r.legalDescription,
          notes: r.notes,
        }));
      await processDocs(parsed);

    } catch (error) {
      console.error(`[${adapter.countyId}] AI import error:`, error);
      toast({
        title: "AI parse failed",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    rawText,
    setRawText,
    isProcessing,
    progress,
    result,
    run,
    runAi,
  };
}
